import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { BackendApi } from '../../../api/api.ts';
import { QueryKey } from '../../../api/queryKeys.ts';
import {
    usePartner__createFixedAssetMutation,
    usePartner__getFixedAssets,
    usePartner__linkFixedAssetToDocumentMutation,
    usePartner__unlinkFixedAssetFromDocumentMutation,
} from '../../../generated/blueprint.ts';
import { fixedAssetLinkErrorMessage, isDocumentWasChanged } from '../../../lib/apiErrors.ts';
import { useNotifications } from '../../../lib/NotificationContext.tsx';
import {
    mapFixedAssetFormToApiBody,
    type FixedAssetFormValues,
} from '../../../pages/app/partner/pages/partnerDashboardPage/components/FixedAssets/fixedAssetForm.ts';
import { useDocumentDrawer } from '../DocumentDrawerProvider.tsx';
import { documentQueryKey } from './documentDrawerApi.ts';

const api = new BackendApi();
const INVALIDATES = [QueryKey.FixedAssets, QueryKey.Documents] as const;

export type LinkableFixedAsset = {
    id: string;
    name: string;
};

type SuccessToast = {
    title: string;
    description: string;
};

type Params = {
    businessId: string;
    documentId: string;
    billVersion: number | null;
    enabled: boolean;
};

export function useBillFixedAsset({ businessId, documentId, billVersion, enabled }: Params) {
    const queryClient = useQueryClient();
    const { notifyDocumentChanged } = useDocumentDrawer();
    const { dispatch } = useNotifications();
    const [isRefreshing, setIsRefreshing] = useState(false);

    const createMutation = usePartner__createFixedAssetMutation(api, { invalidates: INVALIDATES });
    const linkMutation = usePartner__linkFixedAssetToDocumentMutation(api, { invalidates: INVALIDATES });
    const unlinkMutation = usePartner__unlinkFixedAssetFromDocumentMutation(api, { invalidates: INVALIDATES });
    const fixedAssetsQuery = usePartner__getFixedAssets(api, { client_business_id: businessId }, {
        queryKey: QueryKey.FixedAssets,
        enabled,
    });

    const linkableFixedAssets: LinkableFixedAsset[] = (fixedAssetsQuery.data?.items ?? [])
        .filter((item) => item.linked_bill === null)
        .map((item) => ({ id: item.id, name: item.name }));

    async function refreshDocument() {
        await queryClient.refetchQueries({ queryKey: documentQueryKey(businessId, documentId), exact: true });
        notifyDocumentChanged(documentId);
    }

    async function run(action: () => Promise<unknown>, successToast?: SuccessToast) {
        try {
            await action();
        } catch (error: unknown) {
            dispatch({ title: 'Actie mislukt', description: fixedAssetLinkErrorMessage(error), type: 'error' });
            if (isDocumentWasChanged(error)) {
                await refreshDocument().catch(() => undefined);
            }
            throw error;
        }
        setIsRefreshing(true);
        try {
            await refreshDocument();
        } finally {
            setIsRefreshing(false);
        }
        if (successToast) {
            dispatch({ ...successToast, type: 'success' });
        }
    }

    return {
        linkableFixedAssets,
        isLoadingFixedAssets: fixedAssetsQuery.isLoading,
        isBusy: isRefreshing || createMutation.isPending || linkMutation.isPending || unlinkMutation.isPending,
        createFromBill: (values: FixedAssetFormValues) => run(
            () => createMutation.mutateAsync(mapFixedAssetFormToApiBody(businessId, values, {
                documentId,
                expectedBillVersion: billVersion,
            })),
            {
                title: 'Vast actief aangemaakt',
                description: 'Het vast actief is aangemaakt en alle regels zijn op de activarekening geboekt.',
            },
        ),
        linkExisting: (fixedAssetId: string) => run(
            () => linkMutation.mutateAsync({
                client_business_id: businessId,
                document_id: documentId,
                fixed_asset_id: fixedAssetId,
                expected_bill_version: billVersion,
            }),
        ),
        unlink: () => run(
            () => unlinkMutation.mutateAsync({
                client_business_id: businessId,
                document_id: documentId,
                expected_bill_version: billVersion,
            }),
        ),
    };
}
